<template>
  <div class="view-wrapper">
    <div class="view">
      <v-card>
        <v-toolbar class="my-bar">
          <v-toolbar-title>Почты для рассылки</v-toolbar-title>
          <v-spacer />
          <v-text-field
            v-model="search"
            hide-details
            append-icon="search"
            single-line
            dense
            filled
            rounded
            clearable
            class="mr-4"
            label="Поиск по email"
          ></v-text-field>
          <v-btn class="mr-2" color="secondary" @click="openBulkDialog">
            Массовое добавление
          </v-btn>
          <v-btn color="primary" @click="openCreateDialog">
            Добавить
          </v-btn>
        </v-toolbar>

        <v-card-text>
          <v-alert v-if="errorMessage" type="error" dense outlined class="mb-4">
            {{ errorMessage }}
          </v-alert>

          <v-data-table
            :headers="headers"
            :items="filteredItems"
            :loading="loading"
            item-key="id"
            :items-per-page="-1"
            loading-text="Загрузка... Пожалуйста подождите"
            class="elevation-0"
          >
            <template v-slot:item.active="{ item }">
              <v-switch
                v-model="item.active"
                inset
                hide-details
                :disabled="loadingAction"
                @change="saveActive(item)"
              ></v-switch>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn
                icon
                small
                :disabled="loadingAction"
                @click="openEditDialog(item)"
              >
                <v-icon small>edit</v-icon>
              </v-btn>
              <v-btn
                icon
                small
                :disabled="loadingAction"
                @click="openDeleteDialog(item)"
              >
                <v-icon small>remove</v-icon>
              </v-btn>
            </template>

            <template v-slot:no-data>
              <div class="py-6 text-center">
                {{ loading ? "Загрузка..." : "Записей пока нет" }}
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>

      <v-dialog v-model="dialog" max-width="560px" persistent>
        <v-card>
          <v-card-title>
            <span class="headline">
              {{ editedItem.id ? "Редактирование записи" : "Новая запись" }}
            </span>
          </v-card-title>

          <v-card-text>
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-text-field
                v-model.trim="editedItem.email"
                label="Email"
                :rules="emailRules"
                required
                autofocus
              ></v-text-field>
              <v-switch
                v-model="editedItem.active"
                label="Active"
                inset
              ></v-switch>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text :disabled="loadingAction" @click="closeDialog">
              Отмена
            </v-btn>
            <v-btn color="primary" :loading="loadingAction" @click="save">
              Сохранить
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="deleteDialog" max-width="500px">
        <v-card>
          <v-card-title class="headline">
            Вы точно хотите удалить запись?
          </v-card-title>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text :disabled="loadingAction" @click="closeDeleteDialog">
              Отмена
            </v-btn>
            <v-btn color="primary" text :loading="loadingAction" @click="remove">
              OK
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="bulkDialog" max-width="640px" persistent>
        <v-card>
          <v-card-title>
            <span class="headline">Массовое добавление email-ов</span>
          </v-card-title>
          <v-card-text>
            <div class="mb-2">
              Вставьте список email-адресов. Разделители: запятая, пробел, перевод строки.
            </div>
            <v-textarea
              v-model="bulkInput"
              label="Список email"
              auto-grow
              outlined
              rows="5"
              :disabled="loadingAction"
            ></v-textarea>
            <div v-if="bulkErrors.length" class="mt-2 error--text">
              Невалидные email: {{ bulkErrors.join(", ") }}
            </div>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text :disabled="loadingAction" @click="closeBulkDialog">
              Отмена
            </v-btn>
            <v-btn color="primary" :loading="loadingAction" @click="saveBulk">
              Сохранить
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </div>
</template>

<script>
import ctx from "@/ctx";
import notificationMailRecipientsApi from "@/api/notificationMailRecipientsApi";
import "@/types/notificationMailRecipients";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const getDefaultItem = () => ({
  id: undefined,
  email: "",
  active: true
});

export default {
  data: () => ({
    ctx,
    loading: false,
    loadingAction: false,
    errorMessage: "",
    items: [],
    search: "",
    dialog: false,
    deleteDialog: false,
    bulkDialog: false,
    bulkInput: "",
    bulkErrors: [],
    valid: false,
    editedItem: getDefaultItem(),
    deletingItem: undefined,
    headers: [
      { text: "Email", value: "email" },
      { text: "Active", value: "active", sortable: false },
      { text: "Действия", value: "actions", sortable: false, align: "end" }
    ],
    emailRules: [
      v => !!v || "Email обязателен",
      v => EMAIL_REGEX.test(v || "") || "Неверный формат email"
    ]
  }),
  computed: {
    filteredItems() {
      if (!this.search) return this.items;
      const query = this.search.toLowerCase().trim();
      return this.items.filter(item => (item.email || "").toLowerCase().includes(query));
    }
  },
  methods: {
    setSuccess(message) {
      this.errorMessage = "";
      this.ctx.system.msg = message;
    },
    setError(defaultMessage, error) {
      const backendMessage = error?.message;
      const message = backendMessage || defaultMessage;
      this.errorMessage = message;
      this.ctx.system.msg = `[Ошибка]: ${message}`;
    },
    parseEmailsBulk(input) {
      if (!input) return [];
      const parts = input
        .split(/[\s,;]+/g)
        .map(s => s.trim())
        .filter(Boolean);
      const unique = Array.from(new Set(parts));
      const valid = [];
      const invalid = [];
      unique.forEach(e => {
        if (EMAIL_REGEX.test(e)) valid.push(e);
        else invalid.push(e);
      });
      return { valid, invalid };
    },
    async fetchItems() {
      this.loading = true;
      this.errorMessage = "";
      try {
        const res = await notificationMailRecipientsApi.getList();
        this.items = Array.isArray(res)
          ? res.map(r => ({
              id: r.id,
              email: r.email,
              active: !!r.active
            }))
          : [];
      } catch (error) {
        this.setError("Не удалось загрузить список почт", error);
      } finally {
        this.loading = false;
      }
    },
    openBulkDialog() {
      this.bulkInput = "";
      this.bulkErrors = [];
      this.bulkDialog = true;
    },
    closeBulkDialog() {
      this.bulkDialog = false;
      this.bulkInput = "";
      this.bulkErrors = [];
    },
    openCreateDialog() {
      this.editedItem = getDefaultItem();
      this.dialog = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    openEditDialog(item) {
      this.editedItem = {
        id: item.id,
        email: item.email || "",
        active: !!item.active
      };
      this.dialog = true;
      this.$nextTick(() => this.$refs.form && this.$refs.form.resetValidation());
    },
    closeDialog() {
      this.dialog = false;
      this.editedItem = getDefaultItem();
    },
    openDeleteDialog(item) {
      this.deletingItem = item;
      this.deleteDialog = true;
    },
    closeDeleteDialog() {
      this.deleteDialog = false;
      this.deletingItem = undefined;
    },
    async save() {
      if (!this.$refs.form.validate()) return;
      this.loadingAction = true;
      this.errorMessage = "";
      try {
        const payload = {
          email: this.editedItem.email.trim(),
          active: !!this.editedItem.active
        };
        if (this.editedItem.id) payload.id = this.editedItem.id;
        const res = await notificationMailRecipientsApi.save(payload);
        if (!res && res !== 0) {
          throw new Error("Не удалось сохранить запись");
        }
        this.setSuccess(this.editedItem.id ? "Запись обновлена" : "Запись создана");
        this.closeDialog();
        await this.fetchItems();
      } catch (error) {
        this.setError("Ошибка при сохранении записи", error);
      } finally {
        this.loadingAction = false;
      }
    },
    async saveActive(item) {
      this.loadingAction = true;
      try {
        const payload = { id: item.id, email: item.email, active: !!item.active };
        const res = await notificationMailRecipientsApi.save(payload);
        if (!res && res !== 0) throw new Error("Не удалось обновить статус");
        this.setSuccess("Статус обновлён");
      } catch (error) {
        item.active = !item.active;
        this.setError("Ошибка при обновлении статуса", error);
      } finally {
        this.loadingAction = false;
      }
    },
    async saveBulk() {
      const { valid, invalid } = this.parseEmailsBulk(this.bulkInput);
      this.bulkErrors = invalid;
      if (valid.length === 0) {
        this.setError("Нет валидных email для сохранения", { message: invalid.length ? "Проверьте список email" : "" });
        return;
      }
      this.loadingAction = true;
      try {
        const existing = new Set(this.items.map(i => (i.email || "").toLowerCase()));
        const toCreate = valid.filter(e => !existing.has(e.toLowerCase()));
        let successCount = 0;
        for (const email of toCreate) {
          const res = await notificationMailRecipientsApi.save({ email, active: true });
          if (res || res === 0) successCount++;
        }
        this.setSuccess(`Создано записей: ${successCount}${invalid.length ? `. Пропущены невалидные: ${invalid.length}` : ""}`);
        this.closeBulkDialog();
        await this.fetchItems();
      } catch (error) {
        this.setError("Ошибка при массовом сохранении", error);
      } finally {
        this.loadingAction = false;
      }
    },
    async remove() {
      if (!this.deletingItem?.id) return;
      this.loadingAction = true;
      this.errorMessage = "";
      try {
        const res = await notificationMailRecipientsApi.delete(this.deletingItem.id);
        if (!res) {
          throw new Error("Не удалось удалить запись");
        }
        this.setSuccess("Запись удалена");
        this.closeDeleteDialog();
        await this.fetchItems();
      } catch (error) {
        this.setError("Ошибка при удалении записи", error);
      } finally {
        this.loadingAction = false;
      }
    }
  },
  mounted() {
    this.fetchItems();
  }
};
</script>
